// @ts-nocheck
import { useState, useCallback } from 'react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import {
  Stack,
  Stepper,
  Button,
  Group,
  Paper,
  Text,
  Alert,
  FileInput,
  SimpleGrid,
  TextInput,
  Select,
  NumberInput,
  Grid,
  Badge,
  Card,
  ThemeIcon,
  Timeline,
  Loader,
  Center,
  Table,
  Divider,
  CopyButton,
  ActionIcon,
  Tooltip,
  Progress,
  Checkbox,
} from '@mantine/core';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { DatePickerInput } from '@mantine/dates';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { notifications } from '@mantine/notifications';
import {
  IconUpload,
  IconScan,
  IconCheck,
  IconAlertTriangle,
  IconUser,
  IconFileDescription,
  IconLink,
  IconMail,
  IconShieldCheck,
  IconPhoto,
  IconClipboardList,
  IconCopy,
  IconBuildingBank,
  IconArrowRight,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';

/**
 * SmartOnboardingPage — AI-powered employee onboarding.
 *
 * AUTOMATION FLOW (replaces TimeTrex's 15+ manual steps):
 *
 * 1. Admin uploads photo of employee's ID + SSN card
 * 2. AI (Bedrock) extracts: name, DOB, address, SSN, ID type/number
 * 3. System pre-fills W-4, I-9, and employee profile
 * 4. Admin reviews extracted data, corrects if needed
 * 5. System creates employee record + onboarding checklist
 * 6. Employee gets invite link to self-service portal:
 *    - Review pre-filled W-4/I-9
 *    - Add direct deposit (photo of check → routing/account extraction)
 *    - E-sign forms
 *    - Complete remaining items
 *
 * TimeTrex equivalent: 15+ screens, 40+ fields, manual data entry
 * FERP: Upload 2 photos → review → done. Employee handles the rest.
 */

interface ExtractedData {
  firstName: string;
  lastName: string;
  middleName: string;
  dateOfBirth: string;
  ssn: string;
  address: {
    street1: string;
    city: string;
    state: string;
    zipCode: string;
  };
  idType: string;
  idNumber: string;
  idExpiration: string;
  confidence: Record<string, number>;
}

interface OnboardingFormData {
  // Personal (from AI extraction)
  firstName: string;
  lastName: string;
  middleName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  ssn: string;
  address: {
    street1: string;
    city: string;
    state: string;
    zipCode: string;
  };
  // Employment (admin fills)
  hireDate: string;
  jobTitle: string;
  departmentId: string;
  branchId: string;
  managerId: string;
  // Compensation
  payType: string;
  payRate: number;
  payFrequency: string;
  // Tax (auto-derived from extraction)
  filingStatus: string;
  allowances: number;
  workState: string;
  residentState: string;
  // I-9
  idType: string;
  idNumber: string;
  idExpiration: string;
  workAuthorizationType: string;
}

const INITIAL_FORM: OnboardingFormData = {
  firstName: '',
  lastName: '',
  middleName: '',
  email: '',
  phone: '',
  dateOfBirth: '',
  ssn: '',
  address: { street1: '', city: '', state: '', zipCode: '' },
  hireDate: '',
  jobTitle: '',
  departmentId: '',
  branchId: '',
  managerId: '',
  payType: 'hourly',
  payRate: 0,
  payFrequency: 'biweekly',
  filingStatus: 'single',
  allowances: 0,
  workState: '',
  residentState: '',
  idType: '',
  idNumber: '',
  idExpiration: '',
  workAuthorizationType: 'citizen',
};

export default function SmartOnboardingPage() {
  const navigate = useNavigate();
  const [active, setActive] = useState(0);
  const [idFile, setIdFile] = useState<File | null>(null);
  const [ssnFile, setSsnFile] = useState<File | null>(null);
  const [extracting, setExtracting] = useState(false);
  const [extractedData, setExtractedData] = useState<ExtractedData | null>(null);
  const [formData, setFormData] = useState<OnboardingFormData>(INITIAL_FORM);
  const [creating, setCreating] = useState(false);
  const [employeeId, setEmployeeId] = useState<string | null>(null);
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [inviteSent, setInviteSent] = useState(false);
  const [autoChecklist, setAutoChecklist] = useState(true);
  const [autoNewHireReport, setAutoNewHireReport] = useState(true);

  const updateField = useCallback(<K extends keyof OnboardingFormData>(
    field: K,
    value: OnboardingFormData[K],
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  // Simulate AI extraction (in production: calls extractW4FromDocument + extractI9FromDocument mutations)
  const handleExtract = async () => {
    if (!idFile) return;
    setExtracting(true);

    // Simulate Bedrock extraction delay
    await new Promise((resolve) => setTimeout(resolve, 2000));

    // Mock extracted data (in production: from GraphQL mutation response)
    const extracted: ExtractedData = {
      firstName: 'Sarah',
      lastName: 'Johnson',
      middleName: 'Marie',
      dateOfBirth: '1992-03-15',
      ssn: '***-**-4589',
      address: {
        street1: '1234 Oak Street',
        city: 'Portland',
        state: 'OR',
        zipCode: '97201',
      },
      idType: "Driver's License",
      idNumber: '****4521',
      idExpiration: '2027-03-15',
      confidence: {
        firstName: 0.99,
        lastName: 0.99,
        dateOfBirth: 0.97,
        ssn: 0.95,
        address: 0.93,
        idNumber: 0.96,
      },
    };

    setExtractedData(extracted);

    // Pre-fill form with extracted data
    setFormData((prev) => ({
      ...prev,
      firstName: extracted.firstName,
      lastName: extracted.lastName,
      middleName: extracted.middleName,
      dateOfBirth: extracted.dateOfBirth,
      ssn: extracted.ssn,
      address: extracted.address,
      residentState: extracted.address.state,
      workState: extracted.address.state,
      idType: extracted.idType,
      idNumber: extracted.idNumber,
      idExpiration: extracted.idExpiration,
    }));

    setExtracting(false);
    setActive(1); // Auto-advance to review
  };

  const handleCreate = async () => {
    setCreating(true);

    // Simulate employee creation + onboarding setup
    await new Promise((resolve) => setTimeout(resolve, 1500));

    const newId = `emp-${Date.now()}`;
    setEmployeeId(newId);
    setInviteLink(`${window.location.origin}/onboarding/invite/${newId}`);

    setCreating(false);
    setActive(3); // Advance to invite step

    notifications.show({
      title: 'Employee Created',
      message: `${formData.firstName} ${formData.lastName} has been added. Onboarding checklist created.`,
      color: 'green',
    });
  };

  const handleSendInvite = async () => {
    // Simulate sending invite email
    await new Promise((resolve) => setTimeout(resolve, 500));
    setInviteSent(true);
    notifications.show({
      title: 'Invite Sent',
      message: `Onboarding invite sent to ${formData.email}`,
      color: 'green',
    });
  };

  const confidenceColor = (score: number) => {
    if (score >= 0.95) return 'green';
    if (score >= 0.85) return 'yellow';
    return 'red';
  };

  return (
    <Stack gap="md">
      <PageHeader
        title="Smart Onboarding"
        subtitle="AI-powered new hire setup"
        breadcrumbs={[
          { label: 'People', to: '/admin/workforce/employees' },
          { label: 'Smart Onboarding' },
        ]}
      />

      <Paper withBorder p="xl" radius="md">
        <Stepper active={active} onStepClick={setActive} allowNextStepsSelect={false}>
          {/* ── Step 1: Document Capture ──────────────────────────── */}
          <Stepper.Step
            label="Scan Documents"
            description="Upload ID & SSN card"
            icon={<IconPhoto size={18} />}
          >
            <Stack gap="lg" mt="md">
              <Alert icon={<IconScan size={16} />} color="blue" variant="light">
                Upload photos of the employee&apos;s government ID and Social Security card.
                Our AI will extract all required information automatically &mdash; no manual data entry needed.
              </Alert>

              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <Card withBorder p="lg">
                  <Stack gap="md">
                    <Group gap="sm">
                      <ThemeIcon size="lg" variant="light" color="blue">
                        <IconUser size={20} />
                      </ThemeIcon>
                      <div>
                        <Text fw={600}>Government ID</Text>
                        <Text size="xs" c="dimmed">Driver&apos;s license, passport, or state ID</Text>
                      </div>
                    </Group>
                    <FileInput
                      placeholder="Click to upload or drag & drop"
                      accept="image/*,application/pdf"
                      icon={<IconUpload size={16} />}
                      value={idFile}
                      onChange={setIdFile}
                    />
                    {idFile && (
                      <Badge color="green" variant="light" icon={<IconCheck size={12} />}>
                        {idFile.name}
                      </Badge>
                    )}
                  </Stack>
                </Card>

                <Card withBorder p="lg">
                  <Stack gap="md">
                    <Group gap="sm">
                      <ThemeIcon size="lg" variant="light" color="orange">
                        <IconShieldCheck size={20} />
                      </ThemeIcon>
                      <div>
                        <Text fw={600}>Social Security Card</Text>
                        <Text size="xs" c="dimmed">Required for tax withholding setup</Text>
                      </div>
                    </Group>
                    <FileInput
                      placeholder="Click to upload or drag & drop"
                      accept="image/*,application/pdf"
                      icon={<IconUpload size={16} />}
                      value={ssnFile}
                      onChange={setSsnFile}
                    />
                    {ssnFile && (
                      <Badge color="green" variant="light" icon={<IconCheck size={12} />}>
                        {ssnFile.name}
                      </Badge>
                    )}
                  </Stack>
                </Card>
              </SimpleGrid>

              <Group justify="flex-end">
                <Button
                  icon={extracting ? <Loader size={16} color="white" /> : <IconScan size={16} />}
                  onClick={handleExtract}
                  disabled={!idFile || extracting}
                  loading={extracting}
                  size="lg"
                >
                  {extracting ? 'Extracting Data...' : 'Extract & Pre-Fill'}
                </Button>
              </Group>
            </Stack>
          </Stepper.Step>

          {/* ── Step 2: Review Extracted Data ──────────────────────── */}
          <Stepper.Step
            label="Review & Complete"
            description="Verify extracted data"
            icon={<IconFileDescription size={18} />}
          >
            <Stack gap="lg" mt="md">
              {extractedData && (
                <Alert icon={<IconCheck size={16} />} color="green" variant="light">
                  AI extraction complete. Fields with high confidence are pre-filled.
                  Review and add employment details below.
                </Alert>
              )}

              {/* Confidence scores */}
              {extractedData && (
                <Card withBorder p="md">
                  <Text fw={600} mb="sm">Extraction Confidence</Text>
                  <SimpleGrid cols={{ base: 2, sm: 3, md: 6 }}>
                    {Object.entries(extractedData.confidence).map(([field, score]) => (
                      <Group key={field} gap="xs">
                        <Badge size="sm" color={confidenceColor(score)} variant="filled">
                          {Math.round(score * 100)}%
                        </Badge>
                        <Text size="xs" c="dimmed">{field}</Text>
                      </Group>
                    ))}
                  </SimpleGrid>
                </Card>
              )}

              {/* Personal Info (extracted) */}
              <Paper withBorder p="md">
                <Text fw={600} mb="md">Personal Information (from AI extraction)</Text>
                <Grid>
                  <Grid.Col span={{ base: 12, sm: 4 }}>
                    <TextInput
                      label="First Name"
                      required
                      value={formData.firstName}
                      onChange={(e) => updateField('firstName', e.currentTarget.value)}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 4 }}>
                    <TextInput
                      label="Middle Name"
                      value={formData.middleName}
                      onChange={(e) => updateField('middleName', e.currentTarget.value)}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 4 }}>
                    <TextInput
                      label="Last Name"
                      required
                      value={formData.lastName}
                      onChange={(e) => updateField('lastName', e.currentTarget.value)}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <TextInput
                      label="Email"
                      required
                      placeholder="employee@company.com"
                      value={formData.email}
                      onChange={(e) => updateField('email', e.currentTarget.value)}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <TextInput
                      label="Phone"
                      placeholder="(555) 123-4567"
                      value={formData.phone}
                      onChange={(e) => updateField('phone', e.currentTarget.value)}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <TextInput
                      label="Date of Birth"
                      value={formData.dateOfBirth}
                      onChange={(e) => updateField('dateOfBirth', e.currentTarget.value)}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <TextInput
                      label="SSN (masked)"
                      value={formData.ssn}
                      disabled
                      description="Stored encrypted in PII Vault"
                    />
                  </Grid.Col>
                  <Grid.Col span={12}>
                    <TextInput
                      label="Address"
                      value={formData.address.street1}
                      onChange={(e) =>
                        updateField('address', { ...formData.address, street1: e.currentTarget.value })
                      }
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 4 }}>
                    <TextInput
                      label="City"
                      value={formData.address.city}
                      onChange={(e) =>
                        updateField('address', { ...formData.address, city: e.currentTarget.value })
                      }
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 4 }}>
                    <Select
                      label="State"
                      value={formData.address.state}
                      onChange={(v) =>
                        updateField('address', { ...formData.address, state: v ?? '' })
                      }
                      data={[
                        'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
                        'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
                        'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
                        'VA','WA','WV','WI','WY','DC',
                      ]}
                      searchable
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 4 }}>
                    <TextInput
                      label="ZIP Code"
                      value={formData.address.zipCode}
                      onChange={(e) =>
                        updateField('address', { ...formData.address, zipCode: e.currentTarget.value })
                      }
                    />
                  </Grid.Col>
                </Grid>
              </Paper>

              {/* Employment Details (admin fills) */}
              <Paper withBorder p="md">
                <Text fw={600} mb="md">Employment Details</Text>
                <Grid>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <DatePickerInput
                      label="Hire Date"
                      required
                      placeholder="Select date"
                      value={formData.hireDate ? new Date(formData.hireDate) : null}
                      onChange={(date) =>
                        updateField('hireDate', date ? new Date(date as string | Date).toISOString().split('T')[0] ?? '' : '')
                      }
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <TextInput
                      label="Job Title"
                      required
                      placeholder="Software Engineer"
                      value={formData.jobTitle}
                      onChange={(e) => updateField('jobTitle', e.currentTarget.value)}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <Select
                      label="Department"
                      required
                      placeholder="Select"
                      value={formData.departmentId}
                      onChange={(v) => updateField('departmentId', v ?? '')}
                      data={[
                        { value: 'dept-1', label: 'Engineering' },
                        { value: 'dept-2', label: 'Sales' },
                        { value: 'dept-3', label: 'Human Resources' },
                        { value: 'dept-4', label: 'Operations' },
                        { value: 'dept-5', label: 'Finance' },
                      ]}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <Select
                      label="Branch"
                      required
                      placeholder="Select"
                      value={formData.branchId}
                      onChange={(v) => updateField('branchId', v ?? '')}
                      data={[
                        { value: 'branch-1', label: 'Headquarters' },
                        { value: 'branch-2', label: 'West Office' },
                        { value: 'branch-3', label: 'East Office' },
                      ]}
                    />
                  </Grid.Col>
                </Grid>
              </Paper>

              {/* Compensation */}
              <Paper withBorder p="md">
                <Text fw={600} mb="md">Compensation & Tax</Text>
                <Grid>
                  <Grid.Col span={{ base: 12, sm: 4 }}>
                    <Select
                      label="Pay Type"
                      required
                      value={formData.payType}
                      onChange={(v) => updateField('payType', v ?? 'hourly')}
                      data={[
                        { value: 'salary', label: 'Salary' },
                        { value: 'hourly', label: 'Hourly' },
                      ]}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 4 }}>
                    <NumberInput
                      label={formData.payType === 'salary' ? 'Annual Salary' : 'Hourly Rate'}
                      required
                      prefix="$"
                      decimalScale={2}
                      thousandSeparator=","
                      min={0}
                      value={formData.payRate}
                      onChange={(v) => updateField('payRate', typeof v === 'number' ? v : 0)}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 4 }}>
                    <Select
                      label="Pay Frequency"
                      required
                      value={formData.payFrequency}
                      onChange={(v) => updateField('payFrequency', v ?? 'biweekly')}
                      data={[
                        { value: 'weekly', label: 'Weekly' },
                        { value: 'biweekly', label: 'Bi-Weekly' },
                        { value: 'semimonthly', label: 'Semi-Monthly' },
                        { value: 'monthly', label: 'Monthly' },
                      ]}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 4 }}>
                    <Select
                      label="Filing Status (W-4)"
                      required
                      value={formData.filingStatus}
                      onChange={(v) => updateField('filingStatus', v ?? 'single')}
                      data={[
                        { value: 'single', label: 'Single' },
                        { value: 'married_joint', label: 'Married Filing Jointly' },
                        { value: 'married_separate', label: 'Married Filing Separately' },
                        { value: 'head_of_household', label: 'Head of Household' },
                      ]}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 4 }}>
                    <Select
                      label="Work State"
                      value={formData.workState}
                      onChange={(v) => updateField('workState', v ?? '')}
                      data={[
                        'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
                        'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
                        'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
                        'VA','WA','WV','WI','WY','DC',
                      ]}
                      searchable
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 4 }}>
                    <Select
                      label="Work Authorization"
                      value={formData.workAuthorizationType}
                      onChange={(v) => updateField('workAuthorizationType', v ?? 'citizen')}
                      data={[
                        { value: 'citizen', label: 'US Citizen' },
                        { value: 'permanent_resident', label: 'Permanent Resident' },
                        { value: 'work_visa', label: 'Work Visa (H-1B, L-1, etc.)' },
                        { value: 'ead', label: 'Employment Authorization Document' },
                      ]}
                    />
                  </Grid.Col>
                </Grid>
              </Paper>

              {/* Auto-generation options */}
              <Paper withBorder p="md">
                <Text fw={600} mb="md">Automation</Text>
                <Stack gap="sm">
                  <Checkbox
                    label="Auto-create onboarding checklist (W-4 review, I-9 verification, direct deposit setup, policy acknowledgments)"
                    checked={autoChecklist}
                    onChange={(e) => setAutoChecklist(e.currentTarget.checked)}
                  />
                  <Checkbox
                    label="Auto-submit new hire report to state agency (required within 20 days of hire)"
                    checked={autoNewHireReport}
                    onChange={(e) => setAutoNewHireReport(e.currentTarget.checked)}
                  />
                </Stack>
              </Paper>

              <Group justify="space-between">
                <Button variant="default" onClick={() => setActive(0)}>Back</Button>
                <Button
                  onClick={() => setActive(2)}
                  disabled={!formData.firstName || !formData.lastName || !formData.email || !formData.jobTitle}
                >
                  Review Summary
                </Button>
              </Group>
            </Stack>
          </Stepper.Step>

          {/* ── Step 3: Confirm & Create ──────────────────────────── */}
          <Stepper.Step
            label="Confirm"
            description="Review & create"
            icon={<IconClipboardList size={18} />}
          >
            <Stack gap="lg" mt="md">
              <Text fw={600} size="lg">Review New Employee</Text>

              <Paper withBorder p="md">
                <Table>
                  <Table.Tbody>
                    <Table.Tr>
                      <Table.Td fw={600} w={200}>Name</Table.Td>
                      <Table.Td>{formData.firstName} {formData.middleName} {formData.lastName}</Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td fw={600}>Email</Table.Td>
                      <Table.Td>{formData.email}</Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td fw={600}>Address</Table.Td>
                      <Table.Td>
                        {formData.address.street1}, {formData.address.city}, {formData.address.state} {formData.address.zipCode}
                      </Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td fw={600}>Job Title</Table.Td>
                      <Table.Td>{formData.jobTitle}</Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td fw={600}>Pay</Table.Td>
                      <Table.Td>
                        ${formData.payRate.toLocaleString()} {formData.payType === 'salary' ? '/year' : '/hour'} ({formData.payFrequency})
                      </Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td fw={600}>Filing Status</Table.Td>
                      <Table.Td>{formData.filingStatus}</Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td fw={600}>Work Authorization</Table.Td>
                      <Table.Td>{formData.workAuthorizationType}</Table.Td>
                    </Table.Tr>
                  </Table.Tbody>
                </Table>
              </Paper>

              <Card withBorder p="md" bg="var(--mantine-color-blue-0)">
                <Text fw={600} mb="sm">What happens next:</Text>
                <Timeline active={-1} bulletSize={24} lineWidth={2}>
                  <Timeline.Item title="Employee record created">
                    <Text size="sm" c="dimmed">Profile, tax withholding, and I-9 data saved</Text>
                  </Timeline.Item>
                  {autoChecklist && (
                    <Timeline.Item title="Onboarding checklist generated">
                      <Text size="sm" c="dimmed">W-4 review, I-9 sign-off, direct deposit, policy acks</Text>
                    </Timeline.Item>
                  )}
                  {autoNewHireReport && (
                    <Timeline.Item title="New hire report queued">
                      <Text size="sm" c="dimmed">State agency notification within 20-day requirement</Text>
                    </Timeline.Item>
                  )}
                  <Timeline.Item title="Employee receives invite">
                    <Text size="sm" c="dimmed">Self-service portal link to complete remaining items</Text>
                  </Timeline.Item>
                </Timeline>
              </Card>

              <Group justify="space-between">
                <Button variant="default" onClick={() => setActive(1)}>Back</Button>
                <Button
                  color="green"
                  size="lg"
                  icon={<IconCheck size={18} />}
                  onClick={handleCreate}
                  loading={creating}
                >
                  Create Employee & Start Onboarding
                </Button>
              </Group>
            </Stack>
          </Stepper.Step>

          {/* ── Step 4: Invite Employee ──────────────────────────── */}
          <Stepper.Step
            label="Invite"
            description="Send self-service link"
            icon={<IconLink size={18} />}
          >
            <Stack gap="lg" mt="md">
              <Alert icon={<IconCheck size={16} />} color="green" variant="filled">
                {formData.firstName} {formData.lastName} has been created successfully.
                {autoChecklist && ' Onboarding checklist is ready.'}
                {autoNewHireReport && ' New hire report queued for state filing.'}
              </Alert>

              <Card withBorder p="lg">
                <Stack gap="md">
                  <Text fw={600} size="lg">Employee Self-Service Portal</Text>
                  <Text size="sm" c="dimmed">
                    Send the employee a link to complete their onboarding. They can:
                  </Text>
                  <SimpleGrid cols={{ base: 1, sm: 2 }}>
                    <Group gap="xs">
                      <ThemeIcon size="sm" variant="light" color="blue">
                        <IconFileDescription size={14} />
                      </ThemeIcon>
                      <Text size="sm">Review & sign pre-filled W-4</Text>
                    </Group>
                    <Group gap="xs">
                      <ThemeIcon size="sm" variant="light" color="blue">
                        <IconShieldCheck size={14} />
                      </ThemeIcon>
                      <Text size="sm">Review & sign I-9 (Section 1)</Text>
                    </Group>
                    <Group gap="xs">
                      <ThemeIcon size="sm" variant="light" color="blue">
                        <IconBuildingBank size={14} />
                      </ThemeIcon>
                      <Text size="sm">Set up direct deposit (photo of check)</Text>
                    </Group>
                    <Group gap="xs">
                      <ThemeIcon size="sm" variant="light" color="blue">
                        <IconClipboardList size={14} />
                      </ThemeIcon>
                      <Text size="sm">Acknowledge company policies</Text>
                    </Group>
                  </SimpleGrid>

                  <Divider />

                  {inviteLink && (
                    <Group>
                      <TextInput
                        value={inviteLink}
                        readOnly
                        style={{ flex: 1 }}
                        rightSection={
                          <CopyButton value={inviteLink}>
                            {({ copy }) => (
                              <Tooltip label="Copy link">
                                <ActionIcon variant="subtle" onClick={copy}>
                                  <IconCopy size={16} />
                                </ActionIcon>
                              </Tooltip>
                            )}
                          </CopyButton>
                        }
                      />
                    </Group>
                  )}

                  <Group>
                    <Button
                      icon={<IconMail size={16} />}
                      onClick={handleSendInvite}
                      disabled={inviteSent || !formData.email}
                    >
                      {inviteSent ? 'Invite Sent' : `Send Invite to ${formData.email}`}
                    </Button>
                    {inviteSent && (
                      <Badge color="green" variant="light" icon={<IconCheck size={12} />}>
                        Email sent
                      </Badge>
                    )}
                  </Group>
                </Stack>
              </Card>

              {/* Onboarding Progress Preview */}
              <Card withBorder p="md">
                <Group justify="space-between" mb="sm">
                  <Text fw={600}>Onboarding Progress</Text>
                  <Badge color="blue" variant="light">1 / 6 completed</Badge>
                </Group>
                <Progress value={17} color="blue" size="lg" radius="md" mb="sm" />
                <Stack gap="xs">
                  <Group gap="xs">
                    <Badge size="xs" color="green" variant="filled">Done</Badge>
                    <Text size="sm">Employee record created</Text>
                  </Group>
                  <Group gap="xs">
                    <Badge size="xs" color="yellow" variant="light">Pending</Badge>
                    <Text size="sm">W-4 review & signature (employee)</Text>
                  </Group>
                  <Group gap="xs">
                    <Badge size="xs" color="yellow" variant="light">Pending</Badge>
                    <Text size="sm">I-9 Section 1 (employee) + Section 2 (employer)</Text>
                  </Group>
                  <Group gap="xs">
                    <Badge size="xs" color="yellow" variant="light">Pending</Badge>
                    <Text size="sm">Direct deposit setup (employee)</Text>
                  </Group>
                  <Group gap="xs">
                    <Badge size="xs" color="yellow" variant="light">Pending</Badge>
                    <Text size="sm">Policy acknowledgments (employee)</Text>
                  </Group>
                  <Group gap="xs">
                    <Badge size="xs" color="gray" variant="light">Queued</Badge>
                    <Text size="sm">New hire state report filing</Text>
                  </Group>
                </Stack>
              </Card>

              <Group justify="center">
                <Button variant="light" onClick={() => navigate('/admin/workforce/employees')}>
                  View Employees
                </Button>
                <Button variant="light" onClick={() => navigate('/admin/workforce/onboarding')}>
                  View Onboarding
                </Button>
                <Button
                  icon={<IconArrowRight size={16} />}
                  onClick={() => {
                    // Reset form for next employee
                    setActive(0);
                    setFormData(INITIAL_FORM);
                    setExtractedData(null);
                    setIdFile(null);
                    setSsnFile(null);
                    setEmployeeId(null);
                    setInviteLink(null);
                    setInviteSent(false);
                  }}
                >
                  Onboard Another Employee
                </Button>
              </Group>
            </Stack>
          </Stepper.Step>
        </Stepper>
      </Paper>
    </Stack>
  );
}
